import { Html } from '@react-three/drei';
import type { ThreeEvent } from '@react-three/fiber';
import { useMemo } from 'react';
import type { Graph3DNodeRendererProps } from '../../core/renderer.types';
import { createRoundedRectShape } from './nodeShapeUtils';
import { NodeTextHitPlane } from './NodeTextHitPlane';

const HTML_CONTENT_Z_INDEX_RANGE: [number, number] = [160, 0];

export function BadgeNode3D({
  node,
  runtime,
  theme,
  hovered,
  selected,
  focused,
  onHover,
  onSelect,
  onFocus,
}: Graph3DNodeRendererProps) {
  const status = runtime?.status ?? 'idle';
  const active =
    runtime?.active || runtime?.highlighted || hovered || selected || focused;
  const color = runtime?.color ?? theme.runtime[status];
  const fill = active ? '#ffffff' : '#f8fafc';
  const shape = useMemo(
    () =>
      createRoundedRectShape(
        node.resolvedSize.width,
        node.resolvedSize.height,
        12
      ),
    [node.resolvedSize.height, node.resolvedSize.width]
  );
  const extrudeSettings = useMemo(
    () => ({ depth: node.resolvedSize.depth, bevelEnabled: false }),
    [node.resolvedSize.depth]
  );
  const label = runtime?.message || node.label;

  const handleClick = (event: ThreeEvent<MouseEvent>) => {
    event.stopPropagation();
    onSelect(node.id);
    onFocus(node.id);
  };

  return (
    <group
      position={[
        node.resolvedPosition.x,
        node.resolvedPosition.y,
        node.resolvedPosition.z,
      ]}
      onClick={handleClick}
      onPointerEnter={event => {
        event.stopPropagation();
        onHover(node.id);
      }}
      onPointerLeave={event => {
        event.stopPropagation();
        onHover(null);
      }}
    >
      <mesh position={[0, 0, -node.resolvedSize.depth / 2]}>
        <extrudeGeometry args={[shape, extrudeSettings]} />
        <meshStandardMaterial
          color={fill}
          emissive={color}
          emissiveIntensity={active ? 0.24 : 0.08}
          roughness={0.36}
          metalness={0.02}
          transparent
          opacity={runtime?.hidden ? 0.12 : 0.98}
        />
      </mesh>
      <NodeTextHitPlane
        width={node.resolvedSize.width}
        height={node.resolvedSize.height}
        z={node.resolvedSize.depth / 2 + 7}
      />
      <Html
        center
        transform
        distanceFactor={400}
        zIndexRange={HTML_CONTENT_Z_INDEX_RANGE}
        position={[20, 0, node.resolvedSize.depth / 2 + 7]}
      >
        <div
          style={{
            width: node.resolvedSize.width - 18,
            color: active ? '#374151' : '#94a3b8',
            fontSize: 10.5,
            fontWeight: 400,
            lineHeight: '14px',
            textAlign: 'left',
            whiteSpace: 'pre-wrap',
            pointerEvents: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
          }}
        >
          {label}
        </div>
      </Html>
    </group>
  );
}
